import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Wallet, Smartphone, ShoppingBag, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatGyd } from "@/lib/currency";

const MIN_COINS_DEFAULT = 5000; // $50 MMG floor
const DEFAULT_MIN_PER_METHOD = { mmg: 5000, shop: 100, paypal: 5000 };

// Net-ratio fallback if the backend's earnings summary hasn't loaded yet.
// Kept in sync with server.py PAYOUT_NET_RATIO.
const DEFAULT_NET_RATIO = { mmg: 0.5, shop: 1.0, paypal: 1.0 };

/**
 * Two-mode cash-out dialog:
 *   method="mmg"  → MMG mobile money phone; $50 minimum; creator receives
 *                  50% of the requested amount.
 *   method="shop" → Shop credit delivered as a WhatsApp coupon; ANY amount
 *                  ≥ $1; creator receives 100%.
 * Amount is custom, defaults to full balance, capped by balance.
 */
export default function CashOutDialog({
  open,
  method = "mmg",
  onClose,
  balance,
  defaultShopHandle = "",
  netRatios = DEFAULT_NET_RATIO,
  minPerMethod = DEFAULT_MIN_PER_METHOD,
  onSuccess,
}) {
  const [amountStr, setAmountStr] = useState("");
  const [mmgPhone, setMmgPhone] = useState("");
  const [shopPhone, setShopPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const maxUsd = balance / 100;
  const minCoins = minPerMethod?.[method] ?? DEFAULT_MIN_PER_METHOD[method] ?? MIN_COINS_DEFAULT;
  const minUsd = minCoins / 100;
  const balanceEligible = balance >= minCoins;
  const ratio = netRatios?.[method] ?? DEFAULT_NET_RATIO[method] ?? 1;
  const feePct = Math.round((1 - ratio) * 100);

  // Every time the dialog opens (or the method flips), reset the form so the
  // amount defaults to the current full balance in dollars.
  useEffect(() => {
    if (!open) return;
    setAmountStr(maxUsd.toFixed(2));
    setMmgPhone("");
    setShopPhone("");
  }, [open, method, maxUsd, defaultShopHandle]);

  const amountCoins = useMemo(() => {
    const n = parseFloat((amountStr || "").replace(/[^\d.]/g, ""));
    if (Number.isNaN(n) || n <= 0) return 0;
    // 1 coin = 1 cent. Round to whole coins to avoid float drift.
    return Math.round(n * 100);
  }, [amountStr]);

  const amountUsd = amountCoins / 100;
  const netCents = Math.floor(amountCoins * ratio);
  const netUsd = netCents / 100;
  const balanceAfter = balance - amountCoins;
  const isMmg = method === "mmg";
  const brand = isMmg ? "#39FF14" : "#00FFFF";
  const submitLabel = isMmg
    ? `request $${netUsd.toFixed(2)} MMG payout`
    : `request $${amountUsd.toFixed(2)} shop credit`;

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!balanceEligible) {
      toast.error(`Minimum ${method} cash-out is $${minUsd.toFixed(2)} (${minCoins.toLocaleString()} coins)`);
      return;
    }
    if (amountCoins < minCoins) {
      toast.error(`Amount must be at least $${minUsd.toFixed(2)}`);
      return;
    }
    if (amountCoins > balance) {
      toast.error(`Amount exceeds your balance of $${maxUsd.toFixed(2)}`);
      return;
    }
    // Both methods need a phone number now.
    const rawPhone = isMmg ? mmgPhone : shopPhone;
    const digits = (rawPhone || "").replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Enter a phone number (7+ digits)");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        method,
        amount_coins: amountCoins,
      };
      if (isMmg) body.mmg_phone = rawPhone.trim();
      else body.shop_phone = rawPhone.trim();
      const r = await api.post("/me/payouts", body);
      if (isMmg) {
        toast.success(`Payout requested — $${netUsd.toFixed(2)} to MMG within 3 business days.`);
      } else {
        toast.success(`Shop credit requested — $${amountUsd.toFixed(2)} coupon coming via WhatsApp.`);
      }
      onSuccess?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payout request failed");
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = isMmg ? Smartphone : ShoppingBag;

  return (
    <div
      data-testid="cashout-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-[#0A0A0C] border shadow-[0_0_50px_rgba(57,255,20,0.3)]"
        style={{ borderColor: `${brand}80` }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" style={{ color: brand }} strokeWidth={1.8} />
            <span
              className="font-display font-black text-lg tracking-tight glitch"
              data-text="CASH OUT"
              style={{ color: brand }}
            >
              CASH OUT
            </span>
            <span
              data-testid="cashout-method"
              className="ml-2 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest"
              style={{ color: brand, borderColor: `${brand}66` }}
            >
              {isMmg ? "via MMG" : "shop.matrix"}
            </span>
          </div>
          <button
            type="button"
            data-testid="cashout-close"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Amount input — custom */}
          <div className="border border-white/10 p-3 bg-black">
            <label
              htmlFor="cashout-amount"
              className="font-mono text-[10px] uppercase tracking-widest text-white/40"
            >
              redeeming ({amountCoins.toLocaleString()} coins)
            </label>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-white/50 text-2xl font-bold">$</span>
              <input
                id="cashout-amount"
                data-testid="cashout-amount-input"
                type="text"
                inputMode="decimal"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                className="flex-1 bg-transparent font-display font-black text-3xl focus:outline-none w-full"
                style={{ color: brand }}
              />
              <span className="font-mono text-xs text-white/40 uppercase">usd</span>
            </div>
            <div className="mt-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
              <span className="text-white/40">
                balance after: {Math.max(0, balanceAfter).toLocaleString()} coins
              </span>
              <button
                type="button"
                data-testid="cashout-max-btn"
                onClick={() => setAmountStr(maxUsd.toFixed(2))}
                className="text-[#FF007F] hover:text-white"
              >
                max ${maxUsd.toFixed(2)}
              </button>
            </div>
            <p
              data-testid="cashout-amount-gyd"
              className="mt-1 font-mono text-[11px] text-[#00FFFF]/80"
            >
              ≈ <span className="font-bold">{formatGyd(amountUsd)}</span>{" "}
              <span className="text-white/40">// 1 USD = 200 GYD</span>
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/30">
              min ${minUsd.toFixed(2)} · 1 coin = 1 cent · leave any amount to gift later
            </p>
          </div>

          {/* Net payout chip — only visible when there IS a fee (i.e. MMG) */}
          {feePct > 0 && amountCoins >= minCoins && (
            <div
              data-testid="cashout-net-preview"
              className="border p-3 flex items-start gap-2"
              style={{ borderColor: `${brand}55`, backgroundColor: `${brand}0d` }}
            >
              <TrendingDown
                className="h-4 w-4 mt-0.5 flex-shrink-0"
                style={{ color: brand }}
                strokeWidth={2}
              />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                  you receive via MMG
                </p>
                <p
                  data-testid="cashout-net-amount"
                  className="font-display font-black text-2xl"
                  style={{ color: brand }}
                >
                  ${netUsd.toFixed(2)}
                </p>
                <p
                  data-testid="cashout-net-gyd"
                  className="font-mono text-[11px] text-[#00FFFF]/80"
                >
                  ≈ <span className="font-bold">{formatGyd(netUsd)}</span>
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  // {feePct}% platform fee covers MMG transfer costs · pick
                  Shop with Matrix to keep 100%
                </p>
              </div>
            </div>
          )}

          {/* Destination — method-specific */}
          {isMmg ? (
            <div>
              <label
                htmlFor="cashout-mmg-phone"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 flex items-center gap-1.5"
              >
                <Smartphone className="h-3 w-3" /> // mmg.number
              </label>
              <input
                id="cashout-mmg-phone"
                type="tel"
                inputMode="tel"
                required
                data-testid="cashout-mmg-input"
                placeholder="+592 6XX XXXX"
                value={mmgPhone}
                onChange={(e) => setMmgPhone(e.target.value)}
                className="w-full bg-black border border-white/15 focus:border-[#39FF14]/60 focus:outline-none py-3 px-3 text-white"
              />
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/30">
                // the mobile number registered for MMG (Mobile Money Guyana)
              </p>
            </div>
          ) : (
            <div>
              <label
                htmlFor="cashout-shop-phone"
                className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 flex items-center gap-1.5"
              >
                <Smartphone className="h-3 w-3" /> // whatsapp.number
              </label>
              <input
                id="cashout-shop-phone"
                type="tel"
                inputMode="tel"
                required
                data-testid="cashout-shop-input"
                placeholder="+592 6XX XXXX"
                value={shopPhone}
                onChange={(e) => setShopPhone(e.target.value)}
                className="w-full bg-black border border-white/15 focus:border-[#00FFFF]/60 focus:outline-none py-3 px-3 text-white"
              />
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/30">
                // admin sends you a WhatsApp coupon for ios.matrixshopping.com
              </p>
            </div>
          )}

          <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">
            // manual review · funds/coupon typically arrive within 3 business days
          </p>

          <button
            type="submit"
            data-testid="cashout-submit"
            disabled={submitting || !balanceEligible || amountCoins < minCoins || amountCoins > balance}
            className="w-full font-bold uppercase tracking-widest py-4 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
            style={{
              backgroundColor: brand,
              color: "black",
              boxShadow: submitting ? "none" : `0 0 30px ${brand}80`,
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                submitting…
              </>
            ) : (
              <>
                <Icon className="h-4 w-4" strokeWidth={2.5} />
                {submitLabel}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
